/**
 * ProviderCard — one card in the ProviderPicker expanded grid.
 *
 * Renders a single PickerOption as a selectable card with:
 *   - Optional iconUrl (img tag, falls back gracefully)
 *   - Label + description
 *   - Badge (local / 4k / recommended / neutral)
 *   - Pricing + speed row
 *   - Selected state ring
 *   - Disabled state with tooltip
 *   - renderExtras slot for domain-specific footer content
 */

import type { ReactNode } from 'react';
import { Check, Zap, Clock, Star } from 'lucide-react';
import type { PickerOption } from './types';

// =============================================================================
// HELPERS
// =============================================================================

const BADGE_STYLES: Record<string, string> = {
  local: 'bg-blue-900/50 text-blue-300 border border-blue-500/30',
  '4k': 'bg-green-900/50 text-green-300 border border-green-500/30',
  recommended: 'bg-purple-900/50 text-purple-300 border border-purple-500/30',
  neutral: 'bg-white/10 text-white/60 border border-white/20',
};

function SpeedIcon({ speed }: { speed?: PickerOption['speed'] }) {
  switch (speed) {
    case 'fastest':
      return <Zap className="h-3 w-3 text-yellow-400" />;
    case 'fast':
      return <Zap className="h-3 w-3 text-green-400" />;
    case 'medium':
      return <Clock className="h-3 w-3 text-blue-400" />;
    case 'slow':
      return <Clock className="h-3 w-3 text-white/40" />;
    default:
      return null;
  }
}

// =============================================================================
// COMPONENT
// =============================================================================

interface ProviderCardProps<TMeta = unknown> {
  option: PickerOption<TMeta>;
  isSelected: boolean;
  onClick: () => void;
  disabled?: boolean;
  /**
   * Called when a card disabled because the OPTION is unavailable
   * (option.disabled === true, e.g. a local component that isn't installed) is
   * clicked. Lets the caller surface the reason instead of swallowing the click
   * silently. NOT fired when the whole picker is disabled (the `disabled` prop).
   */
  onDisabledClick?: (opt: PickerOption<TMeta>) => void;
  renderExtras?: (opt: PickerOption<TMeta>) => ReactNode;
}

export function ProviderCard<TMeta = unknown>({
  option,
  isSelected,
  onClick,
  disabled = false,
  onDisabledClick,
  renderExtras,
}: ProviderCardProps<TMeta>) {
  const isOptionDisabled = option.disabled === true;
  const isActuallyDisabled = disabled || isOptionDisabled;

  // A plain disabled <button> swallows clicks silently — on touch devices there
  // is no hover tooltip, so tapping an unavailable provider (e.g. ComfyUI when
  // not installed) does nothing with zero feedback. When the card is disabled
  // *because of the option* (not because the whole picker is disabled) and the
  // caller provided onDisabledClick, keep the button interactive so we can route
  // the click to a "why is this disabled" handler (e.g. a toast).
  const interceptDisabledClick = isOptionDisabled && !disabled && !!onDisabledClick;
  const buttonDisabled = isActuallyDisabled && !interceptDisabledClick;
  const handleClick = interceptDisabledClick
    ? () => onDisabledClick?.(option)
    : isActuallyDisabled
      ? undefined
      : onClick;

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={buttonDisabled}
      aria-disabled={isActuallyDisabled}
      title={isActuallyDisabled ? option.disabledReason : undefined}
      className={[
        'relative flex flex-col gap-1 p-3 sm:p-4 rounded-lg border text-left transition-all min-h-[44px] w-full',
        isSelected
          ? 'ring-1 ring-primary-500 border-primary-500 bg-primary-900/30'
          : 'border-white/10 bg-black/30 hover:bg-secondary-700/50 hover:border-white/20',
        isActuallyDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
      ]
        .join(' ')
        .trim()}
    >
      {/* Selected checkmark */}
      {isSelected && (
        <div className="absolute top-2 right-2 w-4 h-4 bg-primary-500 rounded-full flex items-center justify-center">
          <Check className="w-2.5 h-2.5 text-white" />
        </div>
      )}

      {/* Header: icon + label + badge */}
      <div className="flex items-start gap-2 pr-5">
        {option.iconUrl && (
          <img
            src={option.iconUrl}
            alt=""
            aria-hidden="true"
            className="w-7 h-7 rounded-md object-cover shrink-0 mt-0.5"
          />
        )}
        <div className="flex-1 min-w-0">
          <span className="block text-sm font-semibold text-white truncate">{option.label}</span>
          {option.badge && (
            <span
              className={`inline-block mt-0.5 px-1.5 py-px rounded text-[10px] font-bold uppercase ${BADGE_STYLES[option.badge.tone] ?? BADGE_STYLES.neutral}`}
            >
              {option.badge.text}
            </span>
          )}
        </div>
      </div>

      {/* Star rating — shown when quality (1-5) is set; descriptions removed per UX feedback */}
      {option.quality !== undefined && (
        <div className="flex items-center gap-0.5">
          {([1, 2, 3, 4, 5] as const).map((n) => (
            <Star
              key={n}
              className={`h-3 w-3 ${n <= option.quality! ? 'text-yellow-400 fill-yellow-400' : 'text-white/20'}`}
            />
          ))}
        </div>
      )}

      {/* Footer: pricing + speed */}
      {(option.pricing ?? option.speed) && (
        <div className="flex items-center justify-between gap-2 mt-auto pt-1">
          {option.pricing && (
            <span className="text-xs text-white/60 font-medium">{option.pricing}</span>
          )}
          {option.speed && (
            <span className="inline-flex items-center gap-1 text-xs text-white/50 capitalize ml-auto">
              <SpeedIcon speed={option.speed} />
              {option.speed}
            </span>
          )}
        </div>
      )}

      {/* Domain-specific extras slot */}
      {renderExtras && renderExtras(option)}
    </button>
  );
}

export default ProviderCard;
